import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ChatIcon from '@mui/icons-material/Chat';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';

interface ChatNotificationSettingsData {
  id?: number;
  enabled: boolean;
  notification_email: string;
  created_at?: string;
  updated_at?: string | null;
}

const ChatNotificationSettings: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<ChatNotificationSettingsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<ChatNotificationSettingsData>({
    enabled: false,
    notification_email: ''
  });
  
  // Theme for responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      try {
        const response = await api.get('/api/settings/chat-notifications');
        
        if (response.data) {
          setSettings(response.data);
          setFormData(response.data);
        }
      } catch (fetchErr: any) {
        // If the endpoint doesn't exist or returns 404, just use default settings
        // This is normal until the backend API is implemented
        if (fetchErr.response?.status === 404) {
          console.log('Chat notifications API not implemented yet - using default settings');
          // Keep using default settings
        } else {
          throw fetchErr; // Re-throw if it's not a 404 error
        }
      }
    } catch (err: any) {
      console.error('Error fetching chat notification settings:', err);
      setError('Failed to fetch notification settings. This feature may not be fully implemented yet.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof ChatNotificationSettingsData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSwitchChange = (field: keyof ChatNotificationSettingsData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(field, event.target.checked);
  };

  const handleTextChange = (field: keyof ChatNotificationSettingsData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(field, event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      
      try {
        const response = await api.post('/api/settings/chat-notifications', formData);
        
        setSettings(response.data);
        setSuccessMessage('Chat notification settings saved successfully');
      } catch (saveErr: any) {
        if (saveErr.response?.status === 404) {
          // If API endpoint doesn't exist yet, show a more helpful message
          setSuccessMessage('Settings will be saved when the backend is implemented');
          console.log('API endpoint not implemented yet - this is a UI preview');
        } else {
          throw saveErr;
        }
      }
      
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error saving chat notification settings:', err);
      setError('This feature is not fully implemented on the server yet');
    } finally {
      setSaving(false);
    }
  };

  const handleSendTestEmail = async () => {
    try {
      setSaving(true);
      setError(null);
      
      try {
        const response = await api.post('/api/settings/chat-notifications/test', { 
          email: formData.notification_email 
        });
        
        setSuccessMessage('Test email sent successfully');
      } catch (testErr: any) {
        if (testErr.response?.status === 404) {
          // If API endpoint doesn't exist yet, show a more helpful message
          setSuccessMessage('Test email will be available when fully implemented');
          console.log('Test email API endpoint not implemented yet');
        } else {
          throw testErr;
        }
      }
      
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err: any) {
      console.error('Error sending test email:', err);
      setError('This feature is not fully implemented on the server yet');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // When the backend feature is not yet implemented, show a more informative UI
  const isPreviewMode = !settings?.id;

  return (
    <Box sx={{ width: '100%' }}>
      <Card elevation={1} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 2 
          }}>
            <ChatIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">
              Chat Message Notifications
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Configure email notifications for new chat messages. When enabled, an email will be sent
            to the specified address whenever a new message is received.
            {isPreviewMode && (
              <Box component="span" sx={{ color: 'warning.main', ml: 1, fontWeight: 'medium' }}>
                (Preview - backend implementation pending)
              </Box>
            )}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.enabled}
                      onChange={handleSwitchChange('enabled')}
                      color="primary"
                    />
                  }
                  label={formData.enabled ? "Notifications enabled" : "Notifications disabled"}
                />
              </Grid>
              
              <Grid item xs={12} sm={isMobile ? 12 : 8}>
                <TextField
                  label="Notification Email"
                  fullWidth
                  required
                  value={formData.notification_email || ''}
                  onChange={handleTextChange('notification_email')}
                  variant="outlined"
                  size="small"
                  disabled={!formData.enabled}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={isMobile ? 12 : 4}>
                <Button 
                  variant="outlined" 
                  onClick={handleSendTestEmail}
                  disabled={saving || !formData.notification_email || !formData.enabled}
                  fullWidth={isMobile}
                  size="small"
                >
                  Send Test Email
                </Button>
              </Grid>
              
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={saving}
                  size="small"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
      
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={5000} 
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChatNotificationSettings;